import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const RetirementTimeline = ({ currentAge, retirementAge, lifeExpectancy, scenarios }) => {
  const generateTimelineData = () => {
    const data = [];
    const conservativeScenario = scenarios?.find(s => s?.scenario_type === 'conservative');
    const moderateScenario = scenarios?.find(s => s?.scenario_type === 'moderate');
    const optimisticScenario = scenarios?.find(s => s?.scenario_type === 'optimistic');

    for (let age = currentAge; age <= lifeExpectancy; age++) {
      const yearsFromNow = age - currentAge;
      const yearsInRetirement = Math.max(0, age - retirementAge);
      
      data?.push({
        age,
        conservative: conservativeScenario ? 
          Math.max(0, conservativeScenario?.projected_total_savings * (1 - (yearsInRetirement / (conservativeScenario?.years_until_depletion || 1)))) : 0,
        moderate: moderateScenario ? 
          Math.max(0, moderateScenario?.projected_total_savings * (1 - (yearsInRetirement / (moderateScenario?.years_until_depletion || 1)))) : 0,
        optimistic: optimisticScenario ? 
          Math.max(0, optimisticScenario?.projected_total_savings * (1 - (yearsInRetirement / (optimisticScenario?.years_until_depletion || 1)))) : 0
      });
    }

    return data;
  };

  const timelineData = generateTimelineData();

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border rounded-lg p-3 shadow-elevation-2">
          <p className="font-medium text-foreground mb-2">Age {payload?.[0]?.payload?.age}</p>
          <div className="space-y-1">
            {payload?.map((entry, index) => (
              <p key={index} className="text-sm" style={{ color: entry?.color }}>
                {entry?.name}: ${entry?.value?.toLocaleString()}
              </p>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-xl p-6 border shadow-sm">
      <h2 className="text-xl font-semibold text-foreground mb-4">Retirement Timeline Projection</h2>
      <p className="text-sm text-muted-foreground mb-6">
        Projected savings balance from age {currentAge} to {lifeExpectancy}
      </p>

      <div className="w-full h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={timelineData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="age" 
              stroke="var(--color-muted-foreground)"
              label={{ value: 'Age', position: 'insideBottom', offset: -5 }}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
              label={{ value: 'Savings Balance', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />
            <Line 
              type="monotone" 
              dataKey="conservative" 
              stroke="var(--color-error)" 
              strokeWidth={2}
              dot={false}
              name="Conservative"
            />
            <Line 
              type="monotone" 
              dataKey="moderate" 
              stroke="var(--color-warning)" 
              strokeWidth={2}
              dot={false}
              name="Moderate"
            />
            <Line 
              type="monotone" 
              dataKey="optimistic" 
              stroke="var(--color-success)" 
              strokeWidth={2}
              dot={false}
              name="Optimistic"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
          <div className="w-3 h-3 rounded-full bg-error"></div>
          <div>
            <p className="text-xs text-muted-foreground">Conservative</p>
            <p className="text-sm font-medium text-foreground">4% annual return</p>
          </div>
        </div>
        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
          <div className="w-3 h-3 rounded-full bg-warning"></div>
          <div>
            <p className="text-xs text-muted-foreground">Moderate</p>
            <p className="text-sm font-medium text-foreground">6% annual return</p>
          </div>
        </div>
        <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
          <div className="w-3 h-3 rounded-full bg-success"></div>
          <div>
            <p className="text-xs text-muted-foreground">Optimistic</p>
            <p className="text-sm font-medium text-foreground">8% annual return</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetirementTimeline;